/**
 * The guide videos' player: a video with its own controls, so the scrub bar
 * can be cut into chapters, with a list of the chapters underneath and
 * subtitles drawn above the controls. Used by the tutoring Guide page and by
 * the dashboard's "Watch the guide".
 *
 * A video is { src, poster, length, chapters: [[startSeconds, title]], and
 * optionally subtitles (a .vtt) }. The length is given rather than read from
 * the file because nothing is fetched until someone presses play, and the
 * bar is drawn before that.
 *
 *   GuideVideo.html(video)        the markup
 *   GuideVideo.wire(root, video)  bring that markup to life inside root
 *   GuideVideo.open(video, title) show it in a dialog
 */
(function () {
    const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => (
        { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

const VID_ICONS = {
    play: '<path d="M7 4.5v15l13-7.5z" fill="currentColor" stroke="none"/>',
    pause: '<path d="M7 5h3.5v14H7zM13.5 5H17v14h-3.5z" fill="currentColor" stroke="none"/>',
    sound: '<path d="M4 9.5v5h3.5L12 18V6L7.5 9.5z" fill="currentColor" stroke="none"/><path d="M15.5 9a4 4 0 0 1 0 6M18 6.5a7.5 7.5 0 0 1 0 11"/>',
    muted: '<path d="M4 9.5v5h3.5L12 18V6L7.5 9.5z" fill="currentColor" stroke="none"/><path d="M16 9.5l5 5M21 9.5l-5 5"/>',
    full: '<path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5"/>',
    subs: '<rect x="3" y="5.5" width="18" height="13" rx="2.5"/><path d="M7 11h4M13.5 11H17M7 14.5h2.5M12 14.5h5"/>',
};
const vidIcon = (name) => `<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${VID_ICONS[name]}</svg>`;
const vidClock = (t) => `${Math.floor(t / 60)}:${String(Math.floor(t % 60)).padStart(2, '0')}`;
const chapterEnd = (video, i) => (video.chapters[i + 1] ? video.chapters[i + 1][0] : video.length);
const chapterAt = (video, t) => {
    let i = 0;
    while (i < video.chapters.length - 1 && t >= video.chapters[i + 1][0]) i++;
    return i;
};

const html = (video) => `
    <div class="guide-vid" data-vid>
        <video playsinline preload="none" poster="${esc(video.poster)}" src="${esc(video.src)}">
            ${video.subtitles ? `<track kind="subtitles" srclang="en" label="English" src="${esc(video.subtitles)}">` : ''}
        </video>
        <p class="guide-vid-subs" data-vid-subs hidden></p>
        <button type="button" class="guide-vid-big" data-vid-toggle aria-label="Play the guide">${vidIcon('play')}</button>
        <p class="guide-vid-flash" data-vid-flash aria-hidden="true"></p>
        <div class="guide-vid-bar">
            <button type="button" class="guide-vid-btn" data-vid-toggle data-vid-playbtn aria-label="Play">${vidIcon('play')}</button>
            <span class="guide-vid-time" data-vid-time>0:00 / ${vidClock(video.length)}</span>
            <span class="guide-vid-now" data-vid-now>${esc(video.chapters[0][1])}</span>
            ${video.subtitles ? `<button type="button" class="guide-vid-btn" data-vid-subsbtn aria-label="Subtitles" aria-pressed="true">${vidIcon('subs')}</button>` : ''}
            <button type="button" class="guide-vid-btn" data-vid-mute aria-label="Mute">${vidIcon('sound')}</button>
            <button type="button" class="guide-vid-btn" data-vid-full aria-label="Full screen">${vidIcon('full')}</button>
            <div class="guide-vid-track" data-vid-track role="slider" tabindex="0" aria-label="Position in the guide" aria-valuemin="0" aria-valuemax="${Math.round(video.length)}" aria-valuenow="0" aria-valuetext="0:00, ${esc(video.chapters[0][1])}">
                ${video.chapters.map(([start], i) => `<span class="guide-vid-seg" style="flex-grow:${chapterEnd(video, i) - start}"><i></i></span>`).join('')}
                <span class="guide-vid-tip" data-vid-tip hidden></span>
            </div>
        </div>
    </div>
    <ol class="guide-vid-chapters">${video.chapters.map(([start, title], i) => `
        <li><button type="button" data-vid-chapter="${i}"${i === 0 ? ' aria-current="true"' : ''}><span>${vidClock(start)}</span>${esc(title)}</button></li>`).join('')}</ol>`;

function wire(body, video) {
    const box = body.querySelector('[data-vid]');
    if (!box) return;
    const el = box.querySelector('video');
    const $ = (sel) => box.querySelector(sel);
    const track = $('[data-vid-track]');
    const tip = $('[data-vid-tip]');
    const flash = $('[data-vid-flash]');
    const fills = [...box.querySelectorAll('.guide-vid-seg > i')];
    const chapterBtns = [...body.querySelectorAll('[data-vid-chapter]')];
    let chapter = 0;
    let flashTimer = 0;
    // Where a seek made before the file has loaded is waiting to land.
    let pending = null;
    const now = () => (pending != null ? pending : el.currentTime);

    const paint = () => {
        const t = now();
        fills.forEach((fill, i) => {
            const start = video.chapters[i][0];
            fill.style.width = `${Math.max(0, Math.min(1, (t - start) / (chapterEnd(video, i) - start))) * 100}%`;
        });
        $('[data-vid-time]').textContent = `${vidClock(t)} / ${vidClock(video.length)}`;
        const at = chapterAt(video, t);
        track.setAttribute('aria-valuenow', String(Math.round(t)));
        track.setAttribute('aria-valuetext', `${vidClock(t)}, ${video.chapters[at][1]}`);
        if (at === chapter) return;
        chapter = at;
        $('[data-vid-now]').textContent = video.chapters[at][1];
        chapterBtns.forEach((b, i) => (i === at ? b.setAttribute('aria-current', 'true') : b.removeAttribute('aria-current')));
        flash.textContent = video.chapters[at][1];
        flash.classList.add('is-on');
        clearTimeout(flashTimer);
        flashTimer = setTimeout(() => flash.classList.remove('is-on'), 2600);
    };
    const seek = (t) => {
        t = Math.max(0, Math.min(video.length - 0.1, t));
        if (el.readyState >= 1) el.currentTime = t;
        else pending = t;
        paint();
    };
    const play = () => {
        box.classList.add('is-started');
        el.play().catch(() => {});
    };
    const toggle = () => (el.paused ? play() : el.pause());

    el.addEventListener('loadedmetadata', () => {
        if (pending != null) el.currentTime = pending;
        pending = null;
    });
    el.addEventListener('timeupdate', paint);
    el.addEventListener('click', toggle);
    const playState = () => {
        box.classList.toggle('is-playing', !el.paused);
        const btn = $('[data-vid-playbtn]');
        btn.innerHTML = vidIcon(el.paused ? 'play' : 'pause');
        btn.setAttribute('aria-label', el.paused ? 'Play' : 'Pause');
    };
    el.addEventListener('play', playState);
    el.addEventListener('pause', playState);
    box.querySelectorAll('[data-vid-toggle]').forEach((b) => b.addEventListener('click', toggle));

    $('[data-vid-mute]').addEventListener('click', (e) => {
        el.muted = !el.muted;
        e.currentTarget.innerHTML = vidIcon(el.muted ? 'muted' : 'sound');
        e.currentTarget.setAttribute('aria-label', el.muted ? 'Unmute' : 'Mute');
    });
    $('[data-vid-full]').addEventListener('click', () => {
        if (document.fullscreenElement) document.exitFullscreen();
        else if (box.requestFullscreen) box.requestFullscreen().catch(() => {});
        // An iPhone only ever puts the video itself full screen.
        else if (el.webkitEnterFullscreen) el.webkitEnterFullscreen();
    });

    // Subtitles are drawn here rather than by the browser, which would put
    // them underneath the controls. On unless this person turned them off.
    const subs = $('[data-vid-subs]');
    const subsBtn = $('[data-vid-subsbtn]');
    const cues = el.textTracks[0];
    if (cues) {
    let subsOn = true;
    try { subsOn = localStorage.getItem('guideVideoSubs') !== 'off'; } catch { /* private window */ }
    const paintSubs = () => {
        const text = subsOn ? [...(cues.activeCues || [])].map((c) => c.text).join(' ') : '';
        subs.textContent = text;
        subs.hidden = !text;
    };
    cues.mode = 'hidden';
    cues.addEventListener('cuechange', paintSubs);
    subsBtn.setAttribute('aria-pressed', String(subsOn));
    subsBtn.addEventListener('click', () => {
        subsOn = !subsOn;
        subsBtn.setAttribute('aria-pressed', String(subsOn));
        try { localStorage.setItem('guideVideoSubs', subsOn ? 'on' : 'off'); } catch { /* private window */ }
        paintSubs();
    });
    }

    // The bar: hovering names the chapter under the pointer, and pressing
    // or dragging goes there.
    const timeAt = (e) => {
        const r = track.getBoundingClientRect();
        return Math.max(0, Math.min(1, (e.clientX - r.left) / r.width)) * video.length;
    };
    let dragging = false;
    track.addEventListener('pointerdown', (e) => {
        dragging = true;
        track.setPointerCapture(e.pointerId);
        seek(timeAt(e));
    });
    track.addEventListener('pointermove', (e) => {
        const t = timeAt(e);
        if (dragging) seek(t);
        const r = track.getBoundingClientRect();
        tip.textContent = `${vidClock(t)} · ${video.chapters[chapterAt(video, t)][1]}`;
        tip.hidden = false;
        const half = tip.offsetWidth / 2;
        tip.style.left = `${Math.max(half, Math.min(r.width - half, e.clientX - r.left))}px`;
    });
    const endDrag = () => { dragging = false; };
    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);
    track.addEventListener('pointerleave', () => { tip.hidden = true; });
    track.addEventListener('keydown', (e) => {
        const step = { ArrowLeft: -5, ArrowRight: 5, PageDown: -30, PageUp: 30 }[e.key];
        if (e.key === 'Home') seek(0);
        else if (e.key === 'End') seek(video.length);
        else if (step) seek(now() + step);
        else return;
        e.preventDefault();
    });

    chapterBtns.forEach((b) => b.addEventListener('click', () => {
        seek(video.chapters[Number(b.dataset.vidChapter)][0]);
        play();
        box.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }));
}

    // One dialog, built the first time it is wanted and emptied on closing so
    // the video stops.
    let dialog = null;
    let opener = null;
    function close() {
        if (!dialog || dialog.hidden) return;
        dialog.hidden = true;
        dialog.querySelector('[data-guide-body]').innerHTML = '';
        document.removeEventListener('keydown', onKey);
        if (opener && opener.focus) opener.focus();
    }
    function onKey(e) {
        // Escape leaves full screen first; the browser does that by itself.
        if (e.key === 'Escape' && !document.fullscreenElement) close();
    }
    function open(video, title) {
        if (!dialog) {
            dialog = document.createElement('div');
            dialog.className = 'guide-dialog';
            dialog.hidden = true;
            dialog.innerHTML = `
                <div class="guide-dialog-backdrop" data-guide-close></div>
                <div class="guide-dialog-card" role="dialog" aria-modal="true" aria-labelledby="guide-dialog-title">
                    <header class="guide-dialog-head">
                        <h2 id="guide-dialog-title"></h2>
                        <button type="button" class="guide-dialog-x" data-guide-close aria-label="Close">&times;</button>
                    </header>
                    <div data-guide-body></div>
                </div>`;
            document.body.appendChild(dialog);
            dialog.addEventListener('click', (e) => { if (e.target.closest('[data-guide-close]')) close(); });
        }
        opener = document.activeElement;
        dialog.querySelector('#guide-dialog-title').textContent = title;
        const body = dialog.querySelector('[data-guide-body]');
        body.innerHTML = html(video);
        wire(body, video);
        dialog.hidden = false;
        document.addEventListener('keydown', onKey);
        dialog.querySelector('.guide-vid-big').focus();
    }

    window.GuideVideo = { html, wire, open };
})();
